import React, { useEffect, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowSquareOut, Plus, Trash, Lightning } from "@phosphor-icons/react";

const Card = ({ title, children, testid, aside, note }) => (
  <div className="border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-display text-base font-black tracking-tight">{title}</div>
        {note && <p className="mt-1 text-xs text-[#6B7280]">{note}</p>}
      </div>
      {aside}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-bold text-[#6B7280]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const TA = (props) => <textarea {...props} className="w-full rounded-sm border border-[#E5E7EB] p-2 text-sm" />;

export default function AdminSameDay() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: ov } = await api.get("/same-day/admin/overview");
      setData(ov);
      setCfg({ ...ov.config, tier1Text: ov.config.tier1_zips.join(", "), tier2Text: ov.config.tier2_zips.join(", ") });
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setReview = (i, k, v) => set("reviews", cfg.reviews.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        availability_preset: cfg.availability_preset, availability_custom: cfg.availability_custom || "",
        tier1_zips: [cfg.tier1Text], tier2_zips: [cfg.tier2Text],
        premium_text: cfg.premium_text, dispatch_fee: Number(cfg.dispatch_fee) || 0,
        guarantee_enabled: !!cfg.guarantee_enabled, guarantee_line1: cfg.guarantee_line1 || "", guarantee_line2: cfg.guarantee_line2 || "",
        reviews: (cfg.reviews || []).filter((r) => r.name && r.text).map((r) => ({ ...r, rating: Number(r.rating) || 5 })),
        gbp_url: cfg.gbp_url || "",
      };
      await api.put("/same-day/admin/config", payload);
      toast.success("Saved — live on /same-day-cleaning now");
      await load();
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !cfg) return <div className="p-6 text-sm text-[#6B7280]" data-testid="sd-admin-loading">Loading…</div>;
  const { stats, presets } = data;
  const previewText = cfg.availability_preset === "custom" ? (cfg.availability_custom || presets.open) : presets[cfg.availability_preset];

  return (
    <div className="space-y-6 p-6" data-testid="admin-same-day">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight">Same-Day Cleaning</h1>
          <p className="mt-1 text-sm text-[#6B7280]">Availability line, coverage tiers, pricing wording, guarantee and reviews for /same-day-cleaning. Changes are live immediately — no deploy.</p>
        </div>
        <a href="/same-day-cleaning" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold text-[#0044FF]" data-testid="sd-admin-view-page">
          View page <ArrowSquareOut size={14} />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
        {[
          ["Page views (30d)", stats.page_views, "sd-stat-views"], ["Form starts", stats.form_starts, "sd-stat-starts"],
          ["Step 1 done", stats.step1_complete, "sd-stat-step1"], ["Step 2 done", stats.step2_complete, "sd-stat-step2"],
          ["Submits", stats.submits, "sd-stat-submits"], ["Photo upload rate", `${stats.photo_upload_rate}%`, "sd-stat-upload-rate"],
          ["Step-2 drop-off", `${stats.step2_dropoff}%`, "sd-stat-dropoff"], ["Call / text clicks", `${stats.call_clicks} / ${stats.text_clicks}`, "sd-stat-clicks"],
        ].map(([l, v, t]) => (
          <div key={l} className="border border-[#E5E7EB] bg-white p-3" data-testid={t}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">{l}</div>
            <div className="font-display mt-1 text-xl font-black">{v}</div>
          </div>
        ))}
      </div>

      <Card title="Today's availability line" testid="sd-availability-card" note="Shown in the hero. Update daily from the on-call roster. This is a human-set status, not a live indicator.">
        <div className="grid gap-2">
          {[["open", "OPEN"], ["limited", "LIMITED"], ["full", "FULL"], ["custom", "Custom text"]].map(([k, l]) => (
            <label key={k} className={`flex cursor-pointer items-start gap-3 border p-3 text-sm ${cfg.availability_preset === k ? "border-[#182848] bg-[#F6F7FB]" : "border-[#E5E7EB]"}`} data-testid={`sd-preset-${k}`}>
              <input type="radio" name="avail" className="mt-1" checked={cfg.availability_preset === k} onChange={() => set("availability_preset", k)} />
              <span><b>{l}</b>{k !== "custom" && <span className="block text-[#6B7280]">{presets[k]}</span>}</span>
            </label>
          ))}
          {cfg.availability_preset === "custom" && (
            <Input value={cfg.availability_custom || ""} onChange={(e) => set("availability_custom", e.target.value)} placeholder="Today: …" maxLength={200} data-testid="sd-availability-custom" />
          )}
          <div className="mt-2 border-l-4 border-[#1F7A3D] bg-[#E8F5EC] p-3 text-sm font-bold text-[#1F7A3D]" data-testid="sd-availability-preview">{previewText}</div>
        </div>
      </Card>

      <Card title="Coverage tiers (ZIPs)" testid="sd-tiers-card" note="Tier 1 = guaranteed same-day by noon. Tier 2 = best effort. Everything else in Maryland is Tier 3 (next-day). Non-Maryland ZIPs get no submit button.">
        <div className="grid gap-3">
          <Field label="Tier 1 — guaranteed (comma-separated)"><TA rows={3} value={cfg.tier1Text} onChange={(e) => set("tier1Text", e.target.value)} data-testid="sd-tier1" /></Field>
          <Field label="Tier 2 — best effort (comma-separated)"><TA rows={5} value={cfg.tier2Text} onChange={(e) => set("tier2Text", e.target.value)} data-testid="sd-tier2" /></Field>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="What Same-Day Costs" testid="sd-pricing-card" note="No cleaning prices are shown on this page by design. The rule and the dispatch fee must stay visible.">
          <div className="grid gap-3">
            <Field label="Premium wording (Cory to confirm whether a multiplier is published)"><TA rows={4} value={cfg.premium_text} onChange={(e) => set("premium_text", e.target.value)} data-testid="sd-premium" /></Field>
            <Field label="Same-day dispatch fee ($)"><Input type="number" min={0} max={500} value={cfg.dispatch_fee} onChange={(e) => set("dispatch_fee", e.target.value)} data-testid="sd-fee" /></Field>
          </div>
        </Card>
        <Card title="What We Promise" testid="sd-guarantee-card" note="Only promise what dispatch can deliver. Untick to hide the whole section.">
          <div className="grid gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cfg.guarantee_enabled} onChange={(e) => set("guarantee_enabled", e.target.checked)} data-testid="sd-guarantee-enabled" /> Show the guarantee section</label>
            <Field label="Line 1"><TA rows={2} value={cfg.guarantee_line1} onChange={(e) => set("guarantee_line1", e.target.value)} data-testid="sd-guarantee-1" /></Field>
            <Field label="Line 2"><TA rows={2} value={cfg.guarantee_line2} onChange={(e) => set("guarantee_line2", e.target.value)} data-testid="sd-guarantee-2" /></Field>
          </div>
        </Card>
      </div>

      <Card
        title={`Google reviews (${(cfg.reviews || []).length}/5)`}
        testid="sd-reviews-card"
        note="Paste real Google reviews only — first name and neighborhood. With zero reviews the section is hidden on the page."
        aside={<Button size="sm" variant="outline" disabled={(cfg.reviews || []).length >= 5} onClick={() => set("reviews", [...(cfg.reviews || []), { name: "", neighborhood: "", text: "", rating: 5 }])} data-testid="sd-add-review"><Plus size={14} /> Add review</Button>}
      >
        <div className="grid gap-3">
          <Field label="Google Business Profile URL (the 'read all reviews' link)"><Input value={cfg.gbp_url || ""} onChange={(e) => set("gbp_url", e.target.value)} placeholder="https://g.page/…" data-testid="sd-gbp-url" /></Field>
          {(cfg.reviews || []).map((r, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 border border-[#E5E7EB] p-3 md:grid-cols-[1fr_1fr_80px_auto]" data-testid={`sd-review-${i}`}>
              <Field label="First name"><Input value={r.name} onChange={(e) => setReview(i, "name", e.target.value)} data-testid={`sd-review-${i}-name`} /></Field>
              <Field label="Neighborhood"><Input value={r.neighborhood} onChange={(e) => setReview(i, "neighborhood", e.target.value)} data-testid={`sd-review-${i}-hood`} /></Field>
              <Field label="Stars"><Input type="number" min={1} max={5} value={r.rating} onChange={(e) => setReview(i, "rating", e.target.value)} data-testid={`sd-review-${i}-rating`} /></Field>
              <div className="flex items-end"><Button variant="ghost" size="sm" onClick={() => set("reviews", cfg.reviews.filter((_, j) => j !== i))} aria-label="Remove review" data-testid={`sd-review-${i}-remove`}><Trash size={16} /></Button></div>
              <div className="md:col-span-4"><Field label="Review text"><TA rows={2} value={r.text} onChange={(e) => setReview(i, "text", e.target.value)} data-testid={`sd-review-${i}-text`} /></Field></div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="sd-save"><Lightning size={14} weight="fill" /> {saving ? "Saving…" : "Save same-day settings"}</Button>
        {cfg.updated_at && <span className="text-xs text-[#6B7280]">Last saved {new Date(cfg.updated_at).toLocaleString()}</span>}
      </div>

      <Card title="Recent same-day requests" testid="sd-recent">
        {data.recent.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No same-day requests yet.</div>
        ) : (
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]"><th className="py-1">When</th><th>Who</th><th>Needs</th><th>ZIP / tier</th><th>Files</th></tr></thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.request_id} className="border-t border-[#F3F4F6]" data-testid="sd-recent-row">
                    <td className="py-1.5 text-xs text-[#6B7280]">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="font-bold">{r.first_name} <span className="font-normal text-[#6B7280]">{r.phone}</span></td>
                    <td>{r.service} · {r.deadline}{r.deadline_date ? ` ${r.deadline_date}` : ""} · {r.condition}</td>
                    <td className="font-mono">{r.zip} <span className="text-[10px] uppercase text-[#6B7280]">{r.tier}</span></td>
                    <td>{(r.files || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
